import { useIntl } from '@weenat/wintl'
import logEvent from 'app/utils/analytics'
import { capitalize } from 'lodash-es'
import moment from 'moment-timezone'
import PeriodItem from './PeriodItem'

export const SuggestionList: FC<{
  since: number
  before: number
  onChange: (period: { since: number; before: number }) => void
}> = ({ since, before, onChange }) => {
  const { t, formatDateRange } = useIntl()

  const sinceAsMoment = moment.unix(since)
  const beforeAsMoment = moment.unix(before)

  const periodAsDays = beforeAsMoment.diff(sinceAsMoment, 'days')
  const periodAsMonths = beforeAsMoment.diff(sinceAsMoment, 'months')

  let result: { since: number; before: number; label: string; sublabel?: string }[] = []

  const options = {
    dateStyle: 'medium',
    timeStyle: undefined,
    hour12: false
  }

  if (periodAsDays <= 7) {
    const sinceStartOfWeek = sinceAsMoment.startOf('isoWeek').unix()
    const beforeStartOfWeek = beforeAsMoment.startOf('isoWeek').unix()

    result = [
      {
        since: sinceStartOfWeek,
        before: sinceAsMoment.clone().endOf('isoWeek').add(1, 'second').unix(),
        label: `${capitalize(t('timeSteps.7D_no_preprosition'))} ${moment(sinceStartOfWeek * 1000).isoWeek()}`,
        sublabel: formatDateRange(
          sinceAsMoment.startOf('isoWeek').get('year') !==
            sinceAsMoment.endOf('isoWeek').get('year')
            ? sinceAsMoment.clone().startOf('isoWeek').toDate()
            : sinceAsMoment.startOf('isoWeek').get('month') !==
                sinceAsMoment.endOf('isoWeek').get('month')
              ? sinceAsMoment.clone().startOf('isoWeek').toDate()
              : sinceAsMoment.clone().startOf('isoWeek').toDate(),
          sinceAsMoment.endOf('isoWeek').toDate()
        )
      }
    ]

    if (sinceStartOfWeek !== beforeStartOfWeek) {
      result.push({
        since: beforeStartOfWeek,
        before: beforeAsMoment.clone().endOf('isoWeek').add(1, 'second').unix(),
        label: `${capitalize(t('timeSteps.7D_no_preprosition'))} ${moment(beforeStartOfWeek * 1000).isoWeek()}`,
        sublabel: formatDateRange(
          beforeAsMoment.startOf('isoWeek').get('year') !==
            beforeAsMoment.endOf('isoWeek').get('year')
            ? beforeAsMoment.clone().startOf('isoWeek').toDate()
            : beforeAsMoment.startOf('isoWeek').get('month') !==
                beforeAsMoment.endOf('isoWeek').get('month')
              ? beforeAsMoment.clone().startOf('isoWeek').toDate()
              : beforeAsMoment.clone().startOf('isoWeek').toDate(),
          beforeAsMoment.clone().endOf('isoWeek').toDate(),
          options
        )
      })
    }

    result.push({
      since: sinceAsMoment.clone().startOf('month').unix(),
      before: beforeAsMoment.clone().endOf('month').add(1, 'second').unix(),
      label: `${capitalize(sinceAsMoment.format('MMMM YYYY'))}`,
      sublabel: formatDateRange(
        sinceAsMoment.clone().startOf('month').toDate(),
        beforeAsMoment.clone().endOf('month').toDate(),
        options
      )
    })
  } else if (periodAsDays > 7 && periodAsMonths === 0) {
    const sinceStartOfMonth = sinceAsMoment.clone().startOf('month').unix()
    const beforeStartOfMonth = beforeAsMoment.clone().startOf('month').unix()
    result = [
      {
        since: sinceStartOfMonth,
        before: sinceAsMoment.clone().endOf('month').add(1, 'second').unix(),
        label: `${capitalize(sinceAsMoment.format('MMMM YYYY'))}`,
        sublabel: formatDateRange(
          sinceAsMoment.clone().startOf('month').toDate(),
          sinceAsMoment.clone().endOf('month').toDate()
        ),
        options
      }
    ]
    if (sinceStartOfMonth !== beforeStartOfMonth) {
      result.push({
        since: beforeStartOfMonth,
        before: beforeAsMoment.clone().endOf('month').add(1, 'second').unix(),
        label: `${capitalize(moment(beforeStartOfMonth * 1000).format('MMMM YYYY'))}`,
        sublabel: formatDateRange(
          moment(beforeStartOfMonth * 1000)
            .startOf('month')
            .toDate(),
          beforeAsMoment.clone().endOf('month').toDate()
        ),
        options
      })
    }
    result.push({
      since: sinceAsMoment.clone().add(-1, 'month').startOf('month').unix(),
      before: beforeAsMoment.clone().add(1, 'month').endOf('month').add(1, 'second').unix(),
      label: t(
        'periods.custom_with_values_in_hours',
        {
          since: sinceAsMoment.clone().add(-1, 'month').startOf('month').format('MMMM'),
          before: beforeAsMoment.clone().add(1, 'month').endOf('month').format('MMMM')
        },
        { capitalize: true }
      ),
      sublabel: formatDateRange(
        sinceAsMoment.clone().add(-1, 'month').startOf('month').get('year') !==
          beforeAsMoment.clone().add(1, 'month').endOf('month').get('year')
          ? sinceAsMoment.clone().add(-1, 'month').startOf('month').toDate()
          : sinceAsMoment.clone().add(-1, 'month').startOf('month').toDate(),
        beforeAsMoment.clone().add(1, 'month').endOf('month').toDate(),
        options
      )
    })
  } else {
    const sinceStartOfYear = sinceAsMoment.clone().startOf('year').unix()
    const isFullYear =
      sinceAsMoment.clone().startOf('year').unix() === sinceAsMoment.unix() &&
      beforeAsMoment.clone().add(-1, 'second').endOf('year').unix() ===
        beforeAsMoment.add(-1, 'second').unix()

    const sinceEndOfYear = sinceAsMoment.clone().endOf('year').add(1, 'second').unix()
    result = isFullYear
      ? []
      : [
          {
            since: sinceAsMoment.clone().unix(),
            before: beforeAsMoment.clone().unix(),
            label: t(
              'periods.custom_with_values_in_hours',
              {
                since: sinceAsMoment.clone().format('MMMM'),
                before: beforeAsMoment.clone().add(-1, 'second').format('MMMM')
              },
              { capitalize: true }
            ),
            sublabel: formatDateRange(
              sinceAsMoment.get('year') !== beforeAsMoment.get('year')
                ? sinceAsMoment.clone().toDate()
                : sinceAsMoment.clone().toDate(),
              beforeAsMoment.clone().add(-1, 'second').toDate(),
              options
            )
          }
        ]
    result.push({
      since: sinceAsMoment.clone().add(-1, 'month').startOf('month').unix(),
      before: beforeAsMoment.clone().add(1, 'month').endOf('month').unix(),
      label: t(
        'periods.custom_with_values_in_hours',
        {
          since: sinceAsMoment.clone().add(-1, 'month').startOf('month').format('MMMM'),
          before: beforeAsMoment.clone().add(1, 'month').endOf('month').format('MMMM')
        },
        { capitalize: true }
      ),
      sublabel: formatDateRange(
        sinceAsMoment.clone().add(-1, 'month').startOf('month').get('year') !==
          beforeAsMoment.clone().add(1, 'month').endOf('month').get('year')
          ? sinceAsMoment.clone().add(-1, 'month').startOf('month').toDate()
          : sinceAsMoment.clone().add(-1, 'month').startOf('month').toDate(),
        beforeAsMoment.clone().add(1, 'month').endOf('month').toDate(),
        options
      )
    })
    result.push({
      since: sinceStartOfYear,
      before: sinceEndOfYear,
      label: `${sinceAsMoment.year()}`,
      sublabel: formatDateRange(
        sinceAsMoment.get('year') !== beforeAsMoment.get('year')
          ? moment(sinceStartOfYear * 1000).toDate()
          : moment(sinceStartOfYear * 1000).toDate(),
        moment(sinceEndOfYear * 1000)
          .add(-1, 'second')
          .toDate(),
        options
      )
    })
  }

  return (
    <>
      {result.map((item) => (
        <PeriodItem
          key={`${item.since}-${item.before}`}
          onPress={() => {
            logEvent('period_suggestion')
            onChange({ since: item.since, before: item.before })
          }}
          forceActivity={since === item.since && before === item.before}
          label={item.label}
          sublabel={item.sublabel}
        />
      ))}
    </>
  )
}
